<script setup lang="ts">
import { ChevronDown, ClipboardList, Link, Smartphone, UserRound, Wallet } from 'lucide-vue-next'
import ContentEditorFooter from '../../ContentEditorFooter.vue'
import ImageField from '../../ImageField.vue'
import { usePrefetchSurveysForTextareaVars } from '@/composables/usePrefetchSurveysForTextareaVars'
import type { BrandAppNews, BrandAppNewsScreen } from '../../../lib/brandAppContract'
import {
  NEWS_BUTTON_MAX_LENGTH,
  NEWS_DESCRIPTION_MAX_LENGTH,
  NEWS_TITLE_MAX_LENGTH,
  newsErrors,
  type NewsField
} from '../../../lib/brandAppNews'
import useBrandAppConfig from '../../../composables/useBrandAppConfig'
import useContentEditing from '../../../composables/useContentEditing'

const OTHER_SCREENS = ['profile', 'wallet', 'survey'] as const

const props = defineProps<{ news?: BrandAppNews }>()
const emit = defineEmits<{ close: []; save: [news: BrandAppNews] }>()
const { t } = useI18n()
const { tabs } = useBrandAppConfig()
const title = ref(props.news?.title ?? '')
const description = ref(props.news?.description ?? '')
const ctaLabel = ref(props.news?.ctaLabel ?? '')
const destination = ref<'external' | BrandAppNewsScreen | 'survey'>(
  props.news?.ctaTarget.type === 'screen' ? props.news.ctaTarget.screen : 'external'
)
const url = ref(props.news?.ctaTarget.type === 'external' ? props.news.ctaTarget.url : '')
const surveyId = ref<number | null>(
  props.news?.ctaTarget.type === 'screen' && props.news.ctaTarget.screen === 'survey'
    ? props.news.ctaTarget.surveyId
    : null
)
const surveyModel = computed({
  get: () => (surveyId.value === null ? '' : String(surveyId.value)),
  set: (value: string) => {
    surveyId.value = value ? Number(value) : null
  }
})
const {
  surveys,
  isLoading: surveysLoading,
  loadError: surveysError,
  fetchSurveys
} = usePrefetchSurveysForTextareaVars()
const isSurveyUnlisted = computed(
  () => surveyId.value !== null && !!surveys.value && !surveys.value.some((item) => item.id === surveyId.value)
)
watch(
  destination,
  (value) => {
    if (value === 'survey' && !surveys.value && !surveysLoading.value) fetchSurveys()
  },
  { immediate: true }
)
const surveyOptionsReady = computed(
  () =>
    !surveysLoading.value &&
    !surveysError.value &&
    !!surveys.value &&
    (surveys.value.length > 0 || isSurveyUnlisted.value)
)
const destinationLabel = (value: typeof destination.value) =>
  value === 'external'
    ? t('pages.loyaltySupports.webapp.news.external')
    : t(`pages.loyaltySupports.webapp.news.screens.${value}`)
const destinationIcon = (value: typeof destination.value) =>
  value === 'external'
    ? Link
    : value === 'profile'
      ? UserRound
      : value === 'wallet'
        ? Wallet
        : value === 'survey'
          ? ClipboardList
          : Smartphone
const destinationHint = computed(() => {
  if (destination.value === 'survey') {
    if (surveysLoading.value || !surveys.value) return t('pages.loyaltySupports.webapp.news.surveysLoading')
    if (surveysError.value) return t('pages.loyaltySupports.webapp.news.surveysFailed')
    return t('pages.loyaltySupports.webapp.news.surveysEmpty')
  }
  return t('pages.loyaltySupports.webapp.news.screenTarget', { screen: destinationLabel(destination.value) })
})
const id =
  props.news?.id ?? globalThis.crypto?.randomUUID?.() ?? `news-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
const image = ref(props.news?.image ?? '')
const draft = computed<BrandAppNews>(() => ({
  id,
  image: image.value,
  title: title.value,
  description: description.value,
  ctaLabel: ctaLabel.value,
  ctaTarget:
    destination.value === 'external'
      ? { type: 'external', url: url.value.trim() }
      : destination.value === 'survey'
        ? { type: 'screen', screen: 'survey', surveyId: surveyId.value }
        : { type: 'screen', screen: destination.value },
  ...(destination.value === 'external' ? { ctaUrl: url.value.trim() } : {}),
  ...(props.news?.isVisible === undefined ? {} : { isVisible: props.news.isVisible })
}))

const { setDraft } = useContentEditing()
watch(draft, (item) => setDraft({ kind: 'actu', item: { ...item, ctaTarget: { ...item.ctaTarget } } }), {
  immediate: true
})

const attempt = ref<'hide' | 'show' | null>(null)
const fieldErrors = computed<Partial<Record<NewsField, string>>>(() => {
  if (!attempt.value) return {}
  const errors = newsErrors(draft.value, tabs.value)
  return attempt.value === 'show' ? errors : errors.title === 'titleRequired' ? { title: errors.title } : {}
})
const errorOf = (field: NewsField) => {
  const key = fieldErrors.value[field]
  return key ? t(`pages.loyaltySupports.webapp.news.${key}`) : ''
}

const isVisible = ref(props.news?.isVisible !== false)

const save = () => {
  attempt.value = isVisible.value ? 'show' : 'hide'
  if (Object.keys(fieldErrors.value).length) return
  emit('save', {
    ...draft.value,
    isVisible: isVisible.value,
    title: title.value.trim(),
    description: description.value.trim(),
    ctaLabel: ctaLabel.value.trim()
  })
}
</script>

<template>
  <div class="flex flex-col">
    <form id="brand-app-news-form" class="flex min-w-0 flex-col gap-5 px-6 pb-6" @submit.prevent>
      <div class="flex flex-col gap-2">
        <Label for="brandApp.news.image">{{ t('pages.loyaltySupports.webapp.news.image') }} *</Label>
        <ImageField id="brandApp.news.image" v-model="image" :aspect-ratio="4 / 3" crop />
        <p v-if="errorOf('image')" class="text-destructive text-sm">{{ errorOf('image') }}</p>
      </div>

      <div class="flex flex-col gap-2">
        <Label for="brand-app-news-title">{{ t('pages.loyaltySupports.webapp.news.title') }} *</Label>
        <Input
          id="brand-app-news-title"
          v-model="title"
          :maxlength="NEWS_TITLE_MAX_LENGTH"
          :placeholder="t('pages.loyaltySupports.webapp.news.titlePlaceholder')"
        />
        <p v-if="errorOf('title')" class="text-destructive text-sm">{{ errorOf('title') }}</p>
      </div>

      <div class="flex flex-col gap-2">
        <Label for="brand-app-news-description">{{ t('pages.loyaltySupports.webapp.news.description') }} *</Label>
        <Textarea
          id="brand-app-news-description"
          v-model="description"
          class="min-h-28"
          :maxlength="NEWS_DESCRIPTION_MAX_LENGTH"
          :placeholder="t('pages.loyaltySupports.webapp.news.descriptionPlaceholder')"
        />
        <p v-if="errorOf('description')" class="text-destructive text-sm">{{ errorOf('description') }}</p>
      </div>

      <div class="flex flex-col gap-2">
        <Label for="brand-app-news-button">{{ t('pages.loyaltySupports.webapp.news.button') }} *</Label>
        <Input
          id="brand-app-news-button"
          v-model="ctaLabel"
          :maxlength="NEWS_BUTTON_MAX_LENGTH"
          :placeholder="t('pages.loyaltySupports.webapp.news.buttonPlaceholder')"
        />
        <p v-if="errorOf('ctaLabel')" class="text-destructive text-sm">{{ errorOf('ctaLabel') }}</p>
      </div>

      <div class="flex flex-col gap-2">
        <Label :for="destination === 'survey' ? 'brand-app-news-survey' : 'brand-app-news-url'">
          {{ t('pages.loyaltySupports.webapp.news.destination') }} *
        </Label>
        <div class="flex items-stretch">
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <Button
                id="brand-app-news-target"
                type="button"
                variant="outline"
                class="shrink-0 gap-1.5 rounded-r-none text-foreground focus-visible:border-input focus-visible:ring-0"
              >
                <component :is="destinationIcon(destination)" />
                {{ destinationLabel(destination) }}
                <ChevronDown class="text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" class="w-64">
              <DropdownMenuItem @select="destination = 'external'">
                <Link />
                {{ destinationLabel('external') }}
              </DropdownMenuItem>
              <template v-if="tabs.length">
                <DropdownMenuSeparator />
                <DropdownMenuLabel>{{ t('pages.loyaltySupports.webapp.news.screenGroupTabs') }}</DropdownMenuLabel>
                <DropdownMenuItem v-for="tab in tabs" :key="tab" @select="destination = tab">
                  <Smartphone />
                  {{ destinationLabel(tab) }}
                </DropdownMenuItem>
              </template>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>{{ t('pages.loyaltySupports.webapp.news.screenGroupOther') }}</DropdownMenuLabel>
              <DropdownMenuItem v-for="other in OTHER_SCREENS" :key="other" @select="destination = other">
                <component :is="destinationIcon(other)" />
                {{ destinationLabel(other) }}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Input
            v-if="destination === 'external'"
            id="brand-app-news-url"
            v-model="url"
            type="url"
            placeholder="https://"
            class="flex-1 rounded-l-none border-l-0 focus-visible:border-input focus-visible:ring-0"
          />
          <Select v-else-if="destination === 'survey' && surveyOptionsReady" v-model="surveyModel">
            <SelectTrigger id="brand-app-news-survey" class="min-w-0 flex-1 rounded-l-none border-l-0">
              <SelectValue :placeholder="t('pages.loyaltySupports.webapp.news.surveyPlaceholder')" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-if="isSurveyUnlisted" :value="surveyModel">
                {{ t('pages.loyaltySupports.webapp.news.surveyUnavailable') }}
              </SelectItem>
              <SelectItem v-for="item in surveys ?? []" :key="item.id" :value="String(item.id)">
                {{ item.questionText }}
              </SelectItem>
            </SelectContent>
          </Select>
          <Input v-else disabled :model-value="destinationHint" class="flex-1 rounded-l-none border-l-0" />
        </div>
        <div v-if="destination === 'survey' && surveysError" class="flex items-center gap-2">
          <p class="text-destructive text-sm">{{ t('pages.loyaltySupports.webapp.news.surveysFailed') }}</p>
          <Button type="button" variant="outline" size="sm" @click="fetchSurveys">
            {{ t('pages.loyaltySupports.webapp.news.surveysRetry') }}
          </Button>
        </div>
        <p v-if="errorOf('destination')" class="text-destructive text-sm">{{ errorOf('destination') }}</p>
      </div>
    </form>

    <ContentEditorFooter v-model:visible="isVisible" @cancel="emit('close')" @save="save" />
  </div>
</template>
